import { useEffect, useState } from "react";
import { useLang } from "../lib/i18n";

const NavItem = ({ href, children, testId }) => (
    <a href={href} className="nav-link" data-testid={testId}>
        {children}
    </a>
);

export const Nav = () => {
    const { lang, setLang, t } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-cream/80 border-b border-navy/10"
                    : "bg-transparent"
            }`}
            data-testid="main-nav"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
                <a
                    href="#top"
                    className="font-display text-xl md:text-2xl tracking-tight text-navy"
                    data-testid="nav-logo"
                >
                    Gilberto<span className="text-accentblue">.</span>Veliz
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    <NavItem href="#services" testId="nav-services">
                        {t.nav.services}
                    </NavItem>
                    <NavItem href="#experience" testId="nav-experience">
                        {t.nav.experience}
                    </NavItem>
                    <NavItem href="#ebook" testId="nav-ebook">
                        {t.nav.ebook}
                    </NavItem>
                    <NavItem href="#location" testId="nav-location">
                        {t.nav.location}
                    </NavItem>
                    <NavItem href="#contact" testId="nav-contact">
                        {t.nav.contact}
                    </NavItem>
                </nav>

                <div className="flex items-center gap-4">
                    <div
                        className="hidden sm:flex items-center text-xs tracking-widest uppercase text-navy/60"
                        data-testid="lang-toggle"
                        role="group"
                        aria-label="Language toggle"
                    >
                        <button
                            onClick={() => setLang("es")}
                            className={`px-2 py-1 transition-colors ${
                                lang === "es"
                                    ? "text-navy font-medium"
                                    : "hover:text-navy"
                            }`}
                            data-testid="lang-es"
                            aria-pressed={lang === "es"}
                        >
                            ES
                        </button>
                        <span className="text-navy/30">/</span>
                        <button
                            onClick={() => setLang("en")}
                            className={`px-2 py-1 transition-colors ${
                                lang === "en"
                                    ? "text-navy font-medium"
                                    : "hover:text-navy"
                            }`}
                            data-testid="lang-en"
                            aria-pressed={lang === "en"}
                        >
                            EN
                        </button>
                    </div>

                    <a
                        href="#contact"
                        className="hidden md:inline-flex items-center gap-2 bg-navy text-cream text-sm px-5 py-2.5 hover:bg-navy-300 transition-colors"
                        data-testid="nav-cta"
                    >
                        {t.nav.hireMe}
                        <span aria-hidden>→</span>
                    </a>

                    <button
                        className="md:hidden p-2 text-navy"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-btn"
                    >
                        <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                        >
                            {open ? (
                                <path d="M6 6l12 12M18 6L6 18" />
                            ) : (
                                <path d="M3 7h18M3 12h18M3 17h18" />
                            )}
                        </svg>
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    className="md:hidden border-t border-navy/10 bg-cream/95 backdrop-blur-xl"
                    data-testid="mobile-menu"
                >
                    <div className="px-6 py-6 flex flex-col gap-4">
                        <a
                            href="#services"
                            onClick={() => setOpen(false)}
                            className="text-navy/80 text-base"
                            data-testid="mnav-services"
                        >
                            {t.nav.services}
                        </a>
                        <a
                            href="#experience"
                            onClick={() => setOpen(false)}
                            className="text-navy/80 text-base"
                            data-testid="mnav-experience"
                        >
                            {t.nav.experience}
                        </a>
                        <a
                            href="#ebook"
                            onClick={() => setOpen(false)}
                            className="text-navy/80 text-base"
                            data-testid="mnav-ebook"
                        >
                            {t.nav.ebook}
                        </a>
                        <a
                            href="#location"
                            onClick={() => setOpen(false)}
                            className="text-navy/80 text-base"
                            data-testid="mnav-location"
                        >
                            {t.nav.location}
                        </a>
                        <a
                            href="#contact"
                            onClick={() => setOpen(false)}
                            className="text-navy/80 text-base"
                            data-testid="mnav-contact"
                        >
                            {t.nav.contact}
                        </a>
                        <div className="flex items-center gap-3 pt-2 text-xs tracking-widest uppercase">
                            <button
                                onClick={() => setLang("es")}
                                className={
                                    lang === "es" ? "text-navy font-medium" : "text-navy/60"
                                }
                                data-testid="mlang-es"
                            >
                                ES
                            </button>
                            <span className="text-navy/30">/</span>
                            <button
                                onClick={() => setLang("en")}
                                className={
                                    lang === "en" ? "text-navy font-medium" : "text-navy/60"
                                }
                                data-testid="mlang-en"
                            >
                                EN
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Nav;
